import type { Editor, JSONContent } from '@tiptap/react'
import type { Node } from '@xyflow/react'
import { createContext, type MutableRefObject } from 'react'
import type { SetNodes } from '#src/hooks/useFlowStore.ts'

export interface DeskContextType {
  deskId: string
  setNodes: SetNodes | null
  /* The desk's debounced Medplum writeback. Separate from `setNodes` because
   * the two surfaces supply different amounts: the canvas has both, the list
   * view has only this one — it persists node edits without owning a flow
   * store. `useDeskNodeMutation` is the only reader. */
  updateDeskNodes: ((nodes: Node[]) => void) | null
  editorsByNoteIdRef: MutableRefObject<Record<string, Editor>> | null
  /* Body content for notes whose node exists but whose Y.Doc is still empty,
   * keyed `Composition/<nodeId>` — the editor's own field name. The editor
   * seeds itself from this once its provider has synced, which is the only way
   * a newly created note gets a body: the Y.Doc is what renders, and a
   * Composition written alongside it is not read back.
   *
   * `string` as well as `JSONContent` because the two producers differ. The
   * canvas's paste path carries a live editor's JSON; duplicating in the list
   * view carries the source note's persisted `Composition.content`, which is
   * TipTap JSON for notes made in the app but plain HTML for imported ones.
   * `setContent` takes either, so widening here beats round-tripping HTML
   * through `generateJSON` — that conversion is lossy for exactly the custom
   * inline nodes (mentions, observations, choice nodes) a duplicate most needs
   * to keep. */
  initialContentByNoteIdRef: MutableRefObject<
    Record<string, JSONContent | string>
  > | null
}

export const DeskContext = createContext<DeskContextType>({
  deskId: '',
  setNodes: null,
  updateDeskNodes: null,
  editorsByNoteIdRef: null,
  initialContentByNoteIdRef: null,
})
